import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  DEFAULT_ICONS_V2,
  ELEMENT_COLOR,
  ELEMENT_LINK_DESTINATION,
  ELEMENT_POSITIONING,
  ELEMENT_SCOPE,
  ELEMENT_SHAPE,
  ELEMENT_SIZE,
  ELEMENT_STATUS,
  ELEMENT_TEXT_COLOR,
  ELEMENT_TYPE,
  TAB_STATUSES,
} from "@/constants";
import { useUrlParams } from "@/hooks";
import LinkElementIntroIcon from "@/icons/LinkElementIntroIcon.svg";
import { setCurrentElement } from "@/store/actions";
import {
  contentElementsSelector,
  currentElementSelector,
  currentMediaItemSelector,
  elementsSelector,
  visualBuilderDataSelector,
} from "@/store/selectors";

import ElementIntro from "../ElementIntro/ElementIntro";
import LinkForm from "../LinkForm/LinkForm";
import LinkReview from "../LinkReview/LinkReview";

export default function LinkPanel(props) {
  const { onUpdate, onRemove } = props;
  const dispatch = useDispatch();

  const params = useUrlParams();

  const { propertyId, experienceId, tabStatus } = params;

  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));
  const { mediaItems = [] } = visualBuilderData;
  const { mediaItem } = useSelector(currentMediaItemSelector);
  const { id: mediaItemId } = mediaItem;
  const { elements } = useSelector(
    tabStatus === TAB_STATUSES.HOMEBASE ? () => ({ elements: [] }) : elementsSelector(propertyId, experienceId),
  );
  const { elements: contentElements = [] } = useSelector(contentElementsSelector(propertyId, mediaItemId));
  const { element: currentElement } = useSelector(currentElementSelector);

  const scope = tabStatus === TAB_STATUSES.HOMEBASE ? ELEMENT_SCOPE.CONTENT : ELEMENT_SCOPE.EXPERIENCE;
  let filteredElements;

  if (scope === ELEMENT_SCOPE.CONTENT) {
    filteredElements = [...elements, ...contentElements]
      .filter(
        (item) =>
          item.type === ELEMENT_TYPE.LINK &&
          item.scope === scope &&
          mediaItems.find((media) => media.id === item.mediaItemId),
      )
      .sort((a, b) => a.id - b.id);
  } else {
    filteredElements = [...elements, ...contentElements]
      .filter((item) => item.type === ELEMENT_TYPE.LINK && item.scope === scope)
      .sort((a, b) => a.id - b.id);
  }

  const handleElementAdd = async () => {
    const newElement = {
      title: "",
      url: "",
      type: ELEMENT_TYPE.LINK,
      status: ELEMENT_STATUS.DRAFT,
      memoMethod: null,
      linkDestination: ELEMENT_LINK_DESTINATION.MODAL,
      size: ELEMENT_SIZE.MEDIUM,
      shape: ELEMENT_SHAPE.CIRCLE,
      color: ELEMENT_COLOR.DEFAULT,
      textColor: ELEMENT_TEXT_COLOR.LIGHT,
      showText: true,
      scope: ELEMENT_SCOPE.CONTENT,
      positioning: ELEMENT_POSITIONING.CONTENT,
      iconLink: DEFAULT_ICONS_V2.link,
      iconName: ELEMENT_TYPE.LINK,
      xFrameOptions: null,
      helperText: "",
      fileName: "",
      fileSize: null,
    };

    try {
      if (tabStatus === TAB_STATUSES.HOMEBASE) {
        newElement.mediaItemId = mediaItemId;
      }

      if (tabStatus === TAB_STATUSES.CUSTOMIZE) {
        newElement.experienceId = experienceId;
        newElement.mediaItemId = mediaItemId;
        newElement.scope = ELEMENT_SCOPE.EXPERIENCE;
      }

      dispatch(setCurrentElement(newElement));
    } catch {
      toast.error("Oops! Something went wrong...", {
        containerId: "dark",
      });
    }
  };

  return (
    <div data-testid="link-panel">
      {!currentElement.status && !filteredElements.length && (
        <ElementIntro
          title="You don’t have any link elements yet, let’s add one!"
          image={LinkElementIntroIcon}
          onCreate={handleElementAdd}
          data-testid="element-intro"
        />
      )}
      {!currentElement.status && !!filteredElements.length && (
        <LinkReview onCreate={handleElementAdd} onRemove={onRemove} elements={filteredElements} />
      )}
      {!!currentElement.status && (
        <LinkForm
          propertyId={propertyId}
          experienceId={experienceId}
          mediaItemId={mediaItemId}
          tabStatus={tabStatus}
          element={currentElement}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}
